import {
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  forwardRef,
  useEffect,
  useId,
  useRef,
} from 'react';
import s from './design.module.css';
import { Icon, type IconName } from './Icon';

export { Icon };
export type { IconName };

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

export function Button({
  variant = 'secondary',
  small,
  icon,
  busy,
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  small?: boolean;
  icon?: IconName;
  busy?: boolean;
}) {
  const cls = [
    s.btn,
    variant === 'primary' && s.btnPrimary,
    variant === 'secondary' && s.btnSecondary,
    variant === 'ghost' && s.btnGhost,
    variant === 'danger' && s.btnDanger,
    small && s.btnSmall,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button type="button" className={cls} disabled={rest.disabled || busy} {...rest}>
      {busy ? (
        <span className={s.spinner} aria-hidden="true" />
      ) : icon ? (
        <Icon name={icon} size={16} />
      ) : null}
      {children}
    </button>
  );
}

export function Card({
  children,
  tight,
  className,
  style,
}: {
  children: ReactNode;
  tight?: boolean;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <section
      className={[s.card, tight && s.cardTight, className].filter(Boolean).join(' ')}
      style={style}
    >
      {children}
    </section>
  );
}

export type Tone = 'neutral' | 'green' | 'orange' | 'red' | 'blue' | 'purple';

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  const map: Record<Tone, string> = {
    neutral: s.badgeNeutral!,
    green: s.badgeGreen!,
    orange: s.badgeOrange!,
    red: s.badgeRed!,
    blue: s.badgeBlue!,
    purple: s.badgePurple!,
  };
  return <span className={`${s.badge} ${map[tone]}`}>{children}</span>;
}

export function Chip({ children }: { children: ReactNode }) {
  return <span className={s.chip}>{children}</span>;
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className={s.pageHead}>
      <div>
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="flex">{actions}</div>}
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className={s.empty}>{children}</div>;
}

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(function Input({ invalid, className, ...rest }, ref) {
  return (
    <input
      ref={ref}
      className={[s.input, invalid && s.inputError, className].filter(Boolean).join(' ')}
      {...rest}
    />
  );
});

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }
>(function Select({ invalid, className, children, ...rest }, ref) {
  return (
    <select
      ref={ref}
      className={[s.input, invalid && s.inputError, className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </select>
  );
});

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className={s.field}>
      <label className={s.label} htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? (
        <span className={s.error}>{error}</span>
      ) : hint ? (
        <span className={s.hint}>{hint}</span>
      ) : null}
    </div>
  );
}

/** Coloane declarative; `key` e folosit și ca antet implicit. */
export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  width?: string;
  align?: 'left' | 'right';
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selectedKey,
  empty = 'Nimic de afișat.',
  footer,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedKey?: string | null;
  empty?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div>
      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} style={{ width: c.width, textAlign: c.align }}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>
                  <EmptyState>{empty}</EmptyState>
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const k = rowKey(r);
                return (
                  <tr
                    key={k}
                    className={[onRowClick && 'clickable', selectedKey === k && 'selected']
                      .filter(Boolean)
                      .join(' ')}
                    onClick={onRowClick ? () => onRowClick(r) : undefined}
                  >
                    {columns.map((c) => (
                      <td key={c.key} style={{ textAlign: c.align }}>
                        {c.render(r)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {footer && <div className={s.tableFooter}>{footer}</div>}
    </div>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const window = pageNumbers(page, pages);
  return (
    <>
      <span>
        {from}–{to} din {total}
      </span>
      <nav className={s.pager} aria-label="Paginare">
        <button
          type="button"
          className={s.pagerBtn}
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          aria-label="Pagina anterioară"
        >
          <Icon name="chevronLeft" size={14} />
        </button>
        {window.map((p, i) =>
          p === null ? (
            <span key={`gap-${i}`}>…</span>
          ) : (
            <button
              type="button"
              key={p}
              className={s.pagerBtn}
              aria-current={p === page ? 'page' : undefined}
              onClick={() => onPage(p)}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          className={s.pagerBtn}
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          aria-label="Pagina următoare"
        >
          <Icon name="chevronRight" size={14} />
        </button>
      </nav>
    </>
  );
}

/** 1 … 4 5 [6] 7 8 … 20 */
export function pageNumbers(page: number, pages: number): (number | null)[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const set = new Set<number>(
    [1, pages, page - 1, page, page + 1].filter((p) => p >= 1 && p <= pages),
  );
  const sorted = [...set].sort((a, b) => a - b);
  const out: (number | null)[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1]! > 1) out.push(null);
    out.push(p);
  });
  return out;
}

export function Modal({
  title,
  children,
  onClose,
  wide,
  footer,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  // Focusul intră o singură dată în dialog (la montare) și revine la deschidere; Escape închide.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('input, select, textarea, button');
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus();
    };
  }, []);
  return (
    <div className={s.backdrop} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className={[s.modal, wide && s.modalWide].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={ref}
      >
        <div className={s.modalHead}>
          <h2 id={titleId}>{title}</h2>
          <button type="button" onClick={onClose} aria-label="Închide">
            <Icon name="close" />
          </button>
        </div>
        {children}
        {footer && (
          <div className="flex mt" style={{ justifyContent: 'flex-end' }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function Drawer({
  title,
  children,
  onClose,
  footer,
}: {
  title: ReactNode;
  children: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
}) {
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  return (
    <>
      <div className={s.drawerBackdrop} onMouseDown={onClose} />
      <aside className={s.drawer} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className={s.drawerHead}>
          <h2 id={titleId}>{title}</h2>
          <button type="button" onClick={onClose} aria-label="Închide">
            <Icon name="close" />
          </button>
        </div>
        <div className={s.drawerBody}>{children}</div>
        {footer && <div className={s.drawerFoot}>{footer}</div>}
      </aside>
    </>
  );
}

export function Tabs<T extends string>({
  value,
  onChange,
  items,
}: {
  value: T;
  onChange: (v: T) => void;
  items: { value: T; label: string }[];
}) {
  return (
    <div className={s.tabs} role="tablist">
      {items.map((it) => (
        <button
          type="button"
          key={it.value}
          role="tab"
          aria-selected={it.value === value}
          className={s.tab}
          onClick={() => onChange(it.value)}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <span className={s.kbd}>{children}</span>;
}

export const designClasses = s;
